import React from 'react';
import { Calendar, RefreshCw, Zap } from 'lucide-react';
import { format, subDays, startOfMonth } from 'date-fns';

interface DateRangeFilterProps {
  startDate: string;
  endDate: string;
  onDateChange: (start: string, end: string) => void;
  onRefresh: () => void;
  isLoading: boolean;
}

export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  startDate,
  endDate,
  onDateChange,
  onRefresh,
  isLoading,
}) => {
  // Quick button helpers
  const handleQuickSelect = (days: number) => {
    // Note: If endDate is already set, or relative to endDate/today
    const end = new Date(endDate || new Date());
    const start = subDays(end, days - 1);
    onDateChange(format(start, 'yyyy-MM-dd'), format(end, 'yyyy-MM-dd'));
  };

  const handleThisMonth = () => {
    const end = new Date(endDate || new Date());
    const start = startOfMonth(end);
    onDateChange(format(start, 'yyyy-MM-dd'), format(end, 'yyyy-MM-dd'));
  };

  return (
    <div className="bg-white rounded-xl p-4 sm:p-5 border border-slate-200 shadow-sm">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        {/* Date Inputs */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-slate-700 font-medium text-sm">
            <Calendar className="w-4 h-4 text-blue-600" />
            <span>ช่วงวันที่:</span>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative">
              <input
                type="date"
                value={startDate}
                onChange={(e) => onDateChange(e.target.value, endDate)}
                className="px-3 py-1.5 text-sm font-medium border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-slate-700 bg-white shadow-xs"
              />
            </div>
            <span className="text-slate-400 text-sm">ถึง</span>
            <div className="relative">
              <input
                type="date"
                value={endDate}
                onChange={(e) => onDateChange(startDate, e.target.value)}
                className="px-3 py-1.5 text-sm font-medium border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-slate-700 bg-white shadow-xs"
              />
            </div>
          </div>

          {/* Quick Buttons */}
          <div className="flex items-center gap-1.5 pl-0 sm:pl-2 border-t sm:border-t-0 sm:border-l border-slate-200 pt-2 sm:pt-0">
            <span className="text-xs text-slate-400 mr-1 flex items-center gap-1">
              <Zap className="w-3 h-3 text-amber-500" /> ทางลัด:
            </span>
            <button
              type="button"
              onClick={() => handleQuickSelect(7)}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-slate-100 hover:bg-blue-50 hover:text-blue-700 text-slate-700 border border-slate-200 transition-colors"
            >
              7 วัน
            </button>
            <button
              type="button"
              onClick={() => handleQuickSelect(30)}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-slate-100 hover:bg-blue-50 hover:text-blue-700 text-slate-700 border border-slate-200 transition-colors"
            >
              30 วัน
            </button>
            <button
              type="button"
              onClick={handleThisMonth}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-slate-100 hover:bg-blue-50 hover:text-blue-700 text-slate-700 border border-slate-200 transition-colors hidden sm:inline-block"
            >
              เดือนนี้
            </button>
          </div>
        </div>

        {/* Refresh / Submit Button */}
        <div className="flex items-center justify-end">
          <button
            type="button"
            onClick={onRefresh}
            disabled={isLoading}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span>{isLoading ? 'กำลังโหลดข้อมูล...' : 'ดึงข้อมูล'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};
